import { useTranslation } from 'react-i18next';
import { AppLoader } from '@/components/AppLoader';
import { UpgradeCta } from '@/features/billing/components/UpgradeCta';
import { useSubscription } from '@/features/billing/hooks/useSubscription';
import { useDebounceLoading } from '@/hooks/useDebounceLoading';
import {
  EntryMessageForm,
  MessageBubble,
  useEntryModalContext,
} from '@/pages/Schedule/components';
import { useFetchEntryMessages } from '@/pages/Schedule/components/EntryModal/hooks';
import { useScheduleContext } from '@/pages/Schedule/contexts';
import { useDeleteEntryMessage } from '@/pages/Schedule/hooks';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import { MessageStatusType } from '@/types/gql.generated';
import { useToast } from '@/ui';
import { Button, Flex, Tooltip, Text, type FlexProps } from '@/ui';
import type { EntryModalMessage } from '../../../../types';
import { CenteredView } from '../../components/CenteredView';
import { EmptyState } from '../../components/EmptyState';
import { PanelBody } from '../../components/PanelBody';
import { PanelContent } from '../../components/PanelContent';
import { PanelHeader } from '../../components/PanelHeader';
import { PanelTitle } from '../../components/PanelTitle';
import { isInstanceInThePast } from './utils/isInstanceInThePast';

type Props = FlexProps & {
  messages: EntryModalMessage[];
  onBeforeSaveMessage: () => Promise<void>;
};

export const MessagesPanel = ({
  messages,
  onBeforeSaveMessage,
  ...props
}: Props) => {
  const { t } = useTranslation('entryModal', { keyPrefix: 'messages_panel' });
  const { isLoading: isFetchingMessages } = useFetchEntryMessages();
  const toast = useToast();
  const { schedule } = useScheduleContext();
  const { messagesPanel } = useEntryModalContext();

  const { entry, instance, mode } = useDraftEntryStore((state) => ({
    entry: state.draftEntry!,
    instance: state.draftInstance,
    mode: state.draftMode,
  }));

  const { deleteEntryMessage, isPending: isDeletingMessage } =
    useDeleteEntryMessage(entry.id);

  const debouncedIsDeletingMessage = useDebounceLoading(isDeletingMessage);

  const { isProTier } = useSubscription();

  const { activeMessage, clearActiveMessage, createNewMessage, editMessage } =
    messagesPanel;

  const reachedMessageLimit =
    messages.filter((message) => message.status === MessageStatusType.Scheduled)
      .length === schedule?.limits.entryScheduledMessageCount;

  const handleCreateMessage = () => {
    createNewMessage(entry, instance);
  };

  const handleDeleteMessage = (message: EntryModalMessage) => {
    deleteEntryMessage(message.id, {
      onSuccess: () => clearActiveMessage(),
      onError: () => toast.error(t('error_deleting_message_toast')),
    });
  };

  // Avoid showing the loader if messages are fetching due to saving a message
  // on a new item. This scenario causes the message form to unmount in favor
  // of the loader before the form has had a chance to fire its side effects.
  const showLoadingState = isFetchingMessages && !activeMessage;

  return (
    <PanelContent {...props}>
      <PanelHeader>
        <Flex align="center" gap="3">
          <PanelTitle>{t('header')}</PanelTitle>
          <Text color="customgray.mid" fontSize="xs">
            {messages.length}/{schedule?.limits.entryScheduledMessageCount}
          </Text>
        </Flex>
      </PanelHeader>

      <PanelBody>
        {showLoadingState ? (
          <CenteredView>
            <AppLoader />
          </CenteredView>
        ) : !messages.length && isInstanceInThePast(entry, instance) ? (
          <CenteredView>{t('entry_in_past_text')}</CenteredView>
        ) : (mode === 'create' || !instance) && entry.recurrences[0].rule ? (
          <CenteredView>
            {t('recurrence_not_supported.paragraph_one')}
            <br />
            <br />
            {t('recurrence_not_supported.paragraph_two')}
          </CenteredView>
        ) : activeMessage ? (
          <EntryMessageForm
            entry={entry}
            instance={instance}
            message={activeMessage}
            onBeforeSave={onBeforeSaveMessage}
            onCancel={clearActiveMessage}
            onDeleteSuccess={clearActiveMessage}
            onSaveSuccess={clearActiveMessage}
          />
        ) : !isProTier && !messages.length ? (
          <EmptyState
            action={
              <UpgradeCta
                feature="scheduleTextMessages"
                label={t('upgrade_state.call_to_action')}
                variant="button"
              />
            }
          >
            {t('upgrade_state.body')}
          </EmptyState>
        ) : !messages.length ? (
          <EmptyState
            cta={{
              text: t('empty_state.call_to_action'),
              onClick: handleCreateMessage,
            }}
          >
            {t('empty_state.body')}
          </EmptyState>
        ) : (
          <Flex direction="column" flex="1" gap="6">
            {messages.map((message) => (
              <MessageBubble
                isDeletingMessage={debouncedIsDeletingMessage}
                key={message.id}
                message={message}
                onDeleteMessage={handleDeleteMessage}
                onEditMessage={editMessage}
              />
            ))}

            {isProTier ? (
              <Tooltip
                hasArrow
                isDisabled={!reachedMessageLimit}
                label={t('reached_message_limit_tooltip')}
                textAlign="center"
              >
                <Button
                  isDisabled={reachedMessageLimit}
                  variant="secondary"
                  onClick={handleCreateMessage}
                >
                  {t('add_another_message')}
                </Button>
              </Tooltip>
            ) : (
              <Flex align="center" direction="column" gap="4">
                <Text color="customgray.mid" fontSize="sm" textAlign="center">
                  {t('upgrade_state.schedule_more')}
                </Text>
                <UpgradeCta
                  feature="scheduleTextMessages"
                  label={t('upgrade_state.call_to_action')}
                  variant="button"
                />
              </Flex>
            )}
          </Flex>
        )}
      </PanelBody>
    </PanelContent>
  );
};
